import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';

import { useAccountSettings } from '@/entities/account';
import { Box, MenuItem } from '@/shared/ui';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/lib/Button';
import { Loader } from '@/shared/ui/Loader';
import { ControlledSelect } from '@/shared/ui/Select';

import { changeTimezoneSchema } from '../model/schemas';
import {
  ChangeTimezoneDialogProps,
  ChangeTimezoneFormDataType,
} from '../model/types';
import { useUpdateAccountSettings } from '../model/useUpdateAccountSettings';

const CHANGE_TIMEZONE_FORM = 'changeTimezoneForm';
const CHANGE_TIMEZONE_FORM_DIALOG = 'changeTimezoneFormDialog';
const CHANGE_TIMEZONE_SELECT = 'changeTimezoneSelect';

const timezones = [
  'Europe/Kaliningrad',
  'Europe/Moscow',
  'Europe/Samara',
  'Asia/Yekaterinburg',
  'Asia/Omsk',
  'Asia/Krasnoyarsk',
  'Asia/Irkutsk',
  'Asia/Yakutsk',
  'Asia/Vladivostok',
  'Asia/Magadan',
  'Asia/Kamchatka',
];

export const ChangeTimezoneDialog = ({
  open,
  onClose,
}: ChangeTimezoneDialogProps) => {
  const { t } = useTranslation();
  const { accountSettings, isPending: isPendingAccountSettings } =
    useAccountSettings();

  const { updateAccountSettings, isPending: isPendingProfileUpdate } =
    useUpdateAccountSettings();

  const {
    control,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
  } = useForm<ChangeTimezoneFormDataType>({
    resolver: zodResolver(changeTimezoneSchema),
    mode: 'onChange',
    values: {
      timezone: accountSettings?.timezone || timezones[0],
    },
  });

  function handleCloseDialog() {
    onClose?.();
    resetForm();
  }

  const onSubmit = handleSubmit((data) => {
    return updateAccountSettings(data, () => {
      handleCloseDialog();
    });
  });

  const timezoneOptions = timezones.map((tz) => ({
    value: tz,
    label: t(`common:timezone.${tz}`),
  }));

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      id={CHANGE_TIMEZONE_FORM_DIALOG}
      title={t('settings:changeTimezone.title')}
      leftButtonSlot={
        <Button
          onClick={handleCloseDialog}
          variant="outlined"
          disabled={isPendingProfileUpdate}
        >
          {t('common:buttons.cancel')}
        </Button>
      }
      rightButtonSlot={
        <Button
          type="submit"
          form={CHANGE_TIMEZONE_FORM}
          variant="contained"
          color="primary"
          loading={isPendingProfileUpdate}
        >
          {t('common:buttons.save')}
        </Button>
      }
    >
      {isPendingAccountSettings ? (
        <Loader containerSize="extraSmall" />
      ) : (
        <Box
          component="form"
          sx={{ mb: 2, mt: 2 }}
          onSubmit={onSubmit}
          id={CHANGE_TIMEZONE_FORM}
        >
          <ControlledSelect
            control={control}
            name="timezone"
            variant="outlined"
            id={CHANGE_TIMEZONE_SELECT}
            label={t('common:inputs.timezone')}
            disabled={isPendingProfileUpdate}
            errorsMessage={errors.timezone?.message}
          >
            {timezoneOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </ControlledSelect>
        </Box>
      )}
    </Dialog>
  );
};
